// Shared TagsEditor — key/value chip strip with autocomplete suggestions
// drawn from /api/v1/sources/tags/stats (workspace-scoped). Two
// suggestion lists:
//   - while editing the KEY field: keys sorted by usage frequency × recency
//   - while editing the VALUE field with a key set: values previously
//     paired with that exact key (also frequency-ranked)
// Click a suggestion to fill the field; Enter on the value field commits.
//
// Loaded lazily on mount and re-loaded after every commit so freshly-added
// pairs surface for the next entry. Cached for 30s in module scope.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getTagStats } from '../api';
import type { TagPair, TagKey } from '../api';
import { TagChip } from '../lib/tag-colors';

let cache: { ts: number; keys: TagKey[]; pairs: TagPair[] } | null = null;
const CACHE_MS = 30_000;

async function loadStats(force = false): Promise<{ keys: TagKey[]; pairs: TagPair[] }> {
  if (!force && cache && Date.now() - cache.ts < CACHE_MS) return { keys: cache.keys, pairs: cache.pairs };
  try {
    const stats = await getTagStats();
    cache = { ts: Date.now(), ...stats };
    return stats;
  } catch {
    return { keys: [], pairs: [] };
  }
}

export interface TagsEditorProps {
  tags: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /** Compact = onboarding/inline; default style matches the source detail panel. */
  compact?: boolean;
}

export default function TagsEditor({ tags, onChange, compact = false }: TagsEditorProps): React.ReactElement {
  const [k, setK] = useState('');
  const [v, setV] = useState('');
  const [keys, setKeys] = useState<TagKey[]>([]);
  const [pairs, setPairs] = useState<TagPair[]>([]);
  const [focused, setFocused] = useState<'key' | 'value' | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void loadStats().then(s => { setKeys(s.keys); setPairs(s.pairs); });
  }, []);

  function commit(key: string, val: string): void {
    onChange({ ...tags, [key.trim()]: val.trim() });
    setK(''); setV('');
    void loadStats(true).then(s => { setKeys(s.keys); setPairs(s.pairs); });
  }

  // Suggestion lists, filtered by current input. Cap at 8 so the dropdown
  // never gets unwieldy — operators with hundreds of tags should rely on
  // typing to narrow rather than scrolling.
  const keySuggestions = useMemo(() => {
    const q = k.trim().toLowerCase();
    const used = new Set(Object.keys(tags));
    return keys
      .filter(x => !used.has(x.key))
      .filter(x => !q || x.key.toLowerCase().includes(q))
      .slice(0, 8);
  }, [keys, k, tags]);

  const valueSuggestions = useMemo(() => {
    const key = k.trim();
    if (!key) return [];
    const q = v.trim().toLowerCase();
    return pairs
      .filter(p => p.key === key)
      .filter(p => !q || p.value.toLowerCase().includes(q))
      .slice(0, 8);
  }, [pairs, k, v]);

  const showKeyDropdown = focused === 'key' && keySuggestions.length > 0;
  const showValueDropdown = focused === 'value' && valueSuggestions.length > 0;

  function pickKey(key: string): void {
    setK(key);
    // If this key has a single dominant value, pre-fill it; otherwise
    // leave value empty so the user can pick from the value dropdown.
    const matching = pairs.filter(p => p.key === key);
    if (matching.length === 1) setV(matching[0]!.value);
    else setV('');
    setFocused('value');
    requestAnimationFrame(() => valueRef.current?.focus());
  }
  function pickValue(value: string): void {
    if (!k.trim()) return;
    commit(k, value);
    setFocused(null);
  }

  const keyRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click.
  useEffect(() => {
    function onDoc(e: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setFocused(null);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={wrapRef} style={{
      padding: compact ? 10 : 12, marginTop: compact ? 10 : undefined,
      background: 'var(--bg-sunken)', border: '1px solid var(--line)',
      borderRadius: compact ? 8 : 6, position: 'relative',
    }}>
      {Object.entries(tags).length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {Object.entries(tags).map(([key, val]) => (
            <TagChip
              key={key}
              k={key}
              v={val}
              onRemove={() => { const next = { ...tags }; delete next[key]; onChange(next); }}
            />
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, position: 'relative' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            ref={keyRef}
            value={k}
            onChange={e => setK(e.target.value)}
            onFocus={() => setFocused('key')}
            onKeyDown={e => {
              if (e.key === 'Enter' && k.trim() && v.trim()) { e.preventDefault(); commit(k, v); }
              if (e.key === 'Escape') setFocused(null);
            }}
            placeholder="key"
            style={{ width: '100%', padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 12, border: '1px solid var(--line)', borderRadius: 4, outline: 'none', background: 'var(--bg)' }}
          />
          {showKeyDropdown && (
            <SuggestionsDropdown
              items={keySuggestions.map(s => ({
                primary: s.key,
                secondary: `used ${s.count}× · last ${relTime(s.last_seen_at)}`,
                onClick: () => pickKey(s.key),
              }))}
            />
          )}
        </div>
        <span style={{ alignSelf: 'center', color: 'var(--ink-3)' }}>=</span>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            ref={valueRef}
            value={v}
            onChange={e => setV(e.target.value)}
            onFocus={() => setFocused('value')}
            onKeyDown={e => {
              if (e.key === 'Enter' && k.trim() && v.trim()) { e.preventDefault(); commit(k, v); }
              if (e.key === 'Escape') setFocused(null);
            }}
            placeholder="value"
            style={{ width: '100%', padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 12, border: '1px solid var(--line)', borderRadius: 4, outline: 'none', background: 'var(--bg)' }}
          />
          {showValueDropdown && (
            <SuggestionsDropdown
              items={valueSuggestions.map(p => ({
                primary: p.value,
                secondary: `${p.count}× with ${p.key}=`,
                onClick: () => pickValue(p.value),
              }))}
            />
          )}
        </div>
        <button
          className="btn sm"
          disabled={!k.trim() || !v.trim()}
          onClick={() => { if (k.trim() && v.trim()) commit(k, v); }}
        >add</button>
      </div>
    </div>
  );
}

function SuggestionsDropdown({ items }: { items: Array<{ primary: string; secondary: string; onClick: () => void }> }): React.ReactElement {
  return (
    <div style={{
      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
      background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 4,
      boxShadow: '0 4px 12px oklch(0.10 0 0 / 0.14)', zIndex: 30, maxHeight: 260, overflowY: 'auto',
    }}>
      {items.map((it, i) => (
        <button
          key={i}
          onMouseDown={e => { e.preventDefault(); it.onClick(); }}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 12,
            background: 'transparent', color: 'var(--ink)', border: 'none', cursor: 'pointer',
            borderBottom: i < items.length - 1 ? '1px solid var(--line)' : 'none',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-elev)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <div>{it.primary}</div>
          <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{it.secondary}</div>
        </button>
      ))}
    </div>
  );
}

function relTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
